/**
 * V36.77 — Tableau de bord d'historique des relances GoCardless.
 *
 * Affiche :
 *   - 4 KPI : nb relances, taux de succès, escalades, manuelles vs auto
 *   - Graphique d'évolution journalière (success / failed / pending)
 *   - Top 10 motifs d'échec avec barres horizontales
 *   - Table des dernières escalades
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { CheckCircle2, AlertCircle, AlertTriangle, RefreshCw, BarChart3, Calendar, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WINDOWS = [
  { value: 7, label: '7 j' },
  { value: 30, label: '30 j' },
  { value: 90, label: '90 j' },
];

function fmtEUR(cents) {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); } catch { return s; }
}

export default function GoCardlessRetryHistory() {
  const { getAuthHeader } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gocardless/admin/retry-history?days=${days}`, {
        headers: getAuthHeader(),
      });
      setData(r.data);
    } catch (e) {
      toast.error(`Erreur chargement historique : ${e.response?.data?.detail || e.message}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, getAuthHeader]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const totals = data?.totals || {};
  const successRate = totals.success_rate_pct || 0;
  const sparkData = (data?.daily || []).map(d => ({
    ...d, label: fmtDate(d.date),
  }));

  return (
    <div className="space-y-5" data-testid="gc-retry-history-root">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-700 flex items-center justify-center shadow-lg">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-black text-[#0a3d62]" data-testid="gc-retry-history-title">
              Historique des relances — rail GoCardless (existant)
            </h3>
            <p className="text-xs text-slate-500">
              Mesure l'efficacité de l'auto-relance et identifie les patterns d'échec
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map(w => (
            <Button
              key={w.value}
              size="sm"
              variant={days === w.value ? 'default' : 'outline'}
              onClick={() => setDays(w.value)}
              data-testid={`gc-retry-window-${w.value}`}
              className={days === w.value ? 'bg-[#0a3d62] hover:bg-[#0c4a78] text-white' : ''}
            >
              <Calendar className="h-3.5 w-3.5 mr-1" /> {w.label}
            </Button>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={fetchHistory}
            disabled={loading}
            data-testid="gc-retry-history-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
      ) : !data ? (
        <div className="py-12 text-center text-slate-500" data-testid="gc-retry-history-empty">
          Aucune donnée disponible.
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gc-retry-kpi-grid">
            <KpiTile
              icon={RefreshCw}
              tone="indigo"
              label="Relances totales"
              value={totals.nb_retries || 0}
              sub={`${totals.nb_auto_retries || 0} auto · ${totals.nb_manual_retries || 0} manuelles`}
              testid="gc-retry-kpi-total"
            />
            <KpiTile
              icon={CheckCircle2}
              tone="emerald"
              label="Taux de succès"
              value={`${successRate}%`}
              sub={`${totals.nb_success_after_retry || 0} succès / ${totals.nb_retries || 0}`}
              testid="gc-retry-kpi-rate"
            />
            <KpiTile
              icon={AlertCircle}
              tone="rose"
              label="Encore en échec"
              value={totals.nb_failed_after_retry || 0}
              sub={`${totals.nb_pending_after_retry || 0} en attente`}
              testid="gc-retry-kpi-failed"
            />
            <KpiTile
              icon={AlertTriangle}
              tone="amber"
              label="Escalades"
              value={totals.nb_escalations || 0}
              sub="Après 2 tentatives auto"
              testid="gc-retry-kpi-escalations"
            />
          </div>

          {/* Daily chart */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-800 text-sm">Évolution journalière</h4>
              <div className="flex items-center gap-3 text-[10px] text-slate-500">
                <Legend2 color="#10b981" label="Succès" />
                <Legend2 color="#ef4444" label="Échec" />
                <Legend2 color="#94a3b8" label="En attente" />
              </div>
            </div>
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <AreaChart data={sparkData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gOk" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="gKo" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#cbd5e1' }}
                  />
                  <Area type="monotone" dataKey="success" stroke="#10b981" fill="url(#gOk)" name="Succès" />
                  <Area type="monotone" dataKey="failed" stroke="#ef4444" fill="url(#gKo)" name="Échec" />
                  <Area type="monotone" dataKey="pending" stroke="#94a3b8" fill="#94a3b833" name="En attente" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top failure reasons */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="gc-retry-top-reasons">
            <h4 className="font-semibold text-slate-800 text-sm mb-3">
              Motifs d'échec les plus fréquents
            </h4>
            {(data.top_failure_reasons || []).length === 0 ? (
              <p className="text-xs text-slate-500 py-4 text-center">
                Aucun motif d'échec sur cette période. 🎉
              </p>
            ) : (
              <div className="space-y-2">
                {data.top_failure_reasons.map((r, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`gc-retry-reason-${i}`}>
                    <div className="w-6 text-xs font-bold text-slate-400">#{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-700 truncate" title={r.reason}>
                        {r.reason}
                      </div>
                      <div className="h-2 mt-1 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-rose-500 to-rose-400"
                          style={{ width: `${Math.min(100, r.pct)}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-sm font-bold text-rose-700">{r.count}</div>
                      <div className="text-[9px] text-slate-500">{r.pct}%</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent escalations */}
          {(data.recent_escalations || []).length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 shadow-sm" data-testid="gc-retry-escalations">
              <h4 className="font-semibold text-amber-900 text-sm mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> Escalades récentes
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-amber-800 border-b border-amber-200">
                    <tr>
                      <th className="text-left py-1.5">Souscription</th>
                      <th className="text-left py-1.5">Membre</th>
                      <th className="text-center py-1.5">Échéance</th>
                      <th className="text-right py-1.5">Montant</th>
                      <th className="text-left py-1.5">Motif</th>
                      <th className="text-right py-1.5">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_escalations.map((e, i) => (
                      <tr key={i} className="border-b border-amber-100 last:border-0">
                        <td className="py-1.5 font-mono text-[10px] text-slate-700">{e.souscription_id?.slice(0, 12)}…</td>
                        <td className="py-1.5 text-slate-700 truncate max-w-[160px]">{e.user_email || '—'}</td>
                        <td className="py-1.5 text-center font-bold">#{e.index}</td>
                        <td className="py-1.5 text-right font-mono tabular-nums">{fmtEUR(e.amount_cents)}</td>
                        <td className="py-1.5 text-rose-700 truncate max-w-[200px]" title={e.failure_reason}>
                          {e.failure_reason || '—'}
                        </td>
                        <td className="py-1.5 text-right text-slate-500">{fmtDate(e.escalated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiTile({ icon: Icon, tone = 'indigo', label, value, sub, testid }) {
  const tones = {
    indigo: 'from-indigo-600 to-violet-600',
    emerald: 'from-emerald-600 to-emerald-500',
    rose: 'from-rose-600 to-rose-500',
    amber: 'from-amber-500 to-orange-500',
  };
  return (
    <div
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tones[tone]} p-4 text-white shadow-md`}
    >
      <div className="absolute -right-5 -bottom-5 w-20 h-20 rounded-full bg-white/10" />
      <div className="relative flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-white/80 font-bold">{label}</div>
          <div className="text-2xl font-black tabular-nums mt-0.5 truncate">{value}</div>
          {sub && <div className="text-[10px] text-white/80 mt-0.5 truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function Legend2({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
